import type { TreatmentChapter } from "../utils/blueprintTreatmentChapters";
import {
  GRAVITAS_IV_WELLNESS_OFFERINGS,
  GRAVITAS_SKINCARE_PRODUCTS,
} from "./gravitasPricing2026";

export type GravitasAestheticNarrative = {
  reason: string;
  how: string;
  expect: string;
  fit: string;
};

type SkincareRole =
  | "cleanser"
  | "toner"
  | "serum"
  | "moisturizer"
  | "spf"
  | "mask"
  | "scrub"
  | "eye"
  | "acne"
  | "brightening"
  | "renewal"
  | "post_procedure"
  | "regimen"
  | "body"
  | "hair"
  | "fragrance"
  | "prescription"
  | "wound_care"
  | "oil";

function normalizeGravitasAiKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, "'")
    .replace(/\s+/g, " ");
}

function ensureSentence(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

function inferSkincareRole(
  name: string,
  description: string,
): SkincareRole {
  const text = `${name} ${description}`.toLowerCase();
  if (/\b(regimen|collection|essentials)\b/.test(text)) return "regimen";
  if (/\b(at home rx|firm & fade|prescription)\b/.test(text)) return "prescription";
  if (/\b(primocyn|hydrogel|wound)\b/.test(text)) return "wound_care";
  if (/\b(parfum|fragrance|eau de)\b/.test(text)) return "fragrance";
  if (/\b(shampoo|conditioner|hair)\b/.test(text)) return "hair";
  if (/\b(body bar|body bb|body)\b/.test(text)) return "body";
  if (/\b(exolux|regenerative cream|post-procedure|recovery)\b/.test(text))
    return "post_procedure";
  if (/\b(mask|sulfur|pumpkin)\b/.test(text)) return "mask";
  if (/\b(scrub|pumice)\b/.test(text)) return "scrub";
  if (/\b(eye repair|eye cream)\b/.test(text)) return "eye";
  if (/\b(spf|uv protect|zinc uv|broad spectrum|sunscreen)\b/.test(text))
    return "spf";
  if (/\b(cleanser|cleansing milk|oil cleanser)\b/.test(text)) return "cleanser";
  if (/\b(toner|essence)\b/.test(text)) return "toner";
  if (/\b(serum|elixir|oil\b|beauty oil|growth factor|retinol|bakuchiol|vit\.? c|vitamin c)\b/.test(text))
    return "serum";
  if (/\b(moisturizer|biome balance|cream)\b/.test(text)) return "moisturizer";
  if (/\b(acne erase|blemish|acne blend)\b/.test(text)) return "acne";
  if (/\b(brighten|dark spot|secret pearl|secret radiance|secret glow|secret gold|pigment|hyperpigmentation|kojic)\b/.test(text))
    return "brightening";
  if (/\b(renewal|exfoliat|bha|salicylic)\b/.test(text)) return "renewal";
  if (/\b(oil\b|beauty oil)\b/.test(text)) return "oil";
  return "serum";
}

function skincareReason(name: string, role: SkincareRole): string {
  const map: Record<SkincareRole, string> = {
    cleanser: `${name} was included as your daily cleansing step to lift impurities and prepare skin for the rest of your routine.`,
    toner: `${name} was added to rebalance, prep, and refresh skin after cleansing so treatment products absorb more effectively.`,
    serum: `${name} was selected as an active treatment step to target the specific skin goals discussed during your visit.`,
    moisturizer: `${name} was included to support hydration, barrier comfort, and steadier results between visits.`,
    spf: `${name} was added to protect the progress you are building and reduce UV-related pigment, redness, and collagen breakdown.`,
    mask: `${name} was included as a focused treatment mask to clarify, renew, or calm skin on a weekly or as-needed basis.`,
    scrub: `${name} was added for periodic exfoliation to smooth texture and help in-office results look more polished.`,
    eye: `${name} was chosen for the delicate eye area — dark circles, fine lines, and firmness support.`,
    acne: `${name} was selected to help clear breakouts, control oil, and calm congestion without derailing the rest of your plan.`,
    brightening: `${name} was included to brighten uneven tone, fade visible dark spots, and support a more radiant complexion.`,
    renewal: `${name} was added to encourage smoother texture and clearer pores through gentle at-home renewal.`,
    post_procedure: `${name} was included to calm, hydrate, and support skin recovery after professional treatments.`,
    regimen: `${name} was recommended as a coordinated home regimen so cleansing, treatment, and protection work together.`,
    body: `${name} was added to extend brightening and tone-evening support beyond the face.`,
    hair: `${name} was included to support scalp and hair health — especially helpful when blemish-prone skin is a concern.`,
    fragrance: `${name} was added as a finishing touch discussed during your visit.`,
    prescription: `${name} was recommended for prescription-grade pigment support after your provider reviewed your skin needs.`,
    wound_care: `${name} was included for calming, antimicrobial skin support around healing or post-treatment recovery.`,
    oil: `${name} was selected as a nourishing treatment oil to soften lines and support glow without heaviness.`,
  };
  return map[role];
}

function skincareExpect(name: string, role: SkincareRole): string {
  const map: Record<SkincareRole, string> = {
    cleanser: `Use ${name} as directed — typically morning and/or evening. Skin should feel clean and comfortable, not tight or stripped.`,
    toner: `Apply ${name} after cleansing to reset pH and prep skin. You should notice a fresher, smoother surface that absorbs serums more easily.`,
    serum: `Apply ${name} consistently as directed. Active serums build results gradually; give it several weeks of steady use before judging progress.`,
    moisturizer: `${name} should leave skin feeling hydrated and supported. It is meant to make the routine more tolerable while other actives do their work.`,
    spf: `Use ${name} every morning and reapply with real sun exposure. Daily protection is what helps brightening and in-office results last.`,
    mask: `Use ${name} on the schedule your provider set — often weekly or as a boost. Skin should feel clearer, calmer, or more refined after use.`,
    scrub: `Use ${name} as a periodic treatment, not daily. Texture should feel smoother over time without irritation from over-exfoliation.`,
    eye: `Pat ${name} gently around the eye area as directed. Benefits are cumulative — firmer, brighter-looking eyes build with consistency.`,
    acne: `Use ${name} as directed and avoid stacking too many strong actives at once. Clearer-looking skin usually builds over several weeks.`,
    brightening: `Pigment improvement with ${name} is gradual. Pair it with daily SPF for the most even, lasting brightening results.`,
    renewal: `Start ${name} exactly as directed — renewal products can irritate if overused. Smoother texture and clarity usually build over time.`,
    post_procedure: `${name} should feel soothing and supportive after treatments. Use daily to calm redness, hydrate, and help results last longer.`,
    regimen: `Follow the regimen steps in order: cleanse, treat, moisturize, and protect. Consistency across the set is what delivers the glow your provider mapped out.`,
    body: `Apply ${name} to body areas as directed. Tone-evening on the body is gradual — steady use plus sun protection gives the best payoff.`,
    hair: `Use ${name} regularly as part of your shower routine. Hair should feel cleaner, softer, and less weighed down over time.`,
    fragrance: `${name} is a personal finishing step — wear it as you like for the scent profile discussed with your provider.`,
    prescription: `Your provider will guide timing and application for ${name}. Prescription fading is gradual; follow their check-in schedule for best results.`,
    wound_care: `Apply ${name} as directed to supported areas. It is designed to calm and protect skin during healing without harsh irritation.`,
    oil: `${name} absorbs quickly and layers well under makeup. Use consistently for softer texture and a smoother, more luminous finish.`,
  };
  return map[role];
}

function skincareFit(name: string, role: SkincareRole): string {
  const map: Record<SkincareRole, string> = {
    cleanser: `${name} is the foundation of your home routine — every active product works better from clean, balanced skin.`,
    toner: `${name} bridges cleansing and treatment, helping the rest of your plan absorb and perform more predictably.`,
    serum: `${name} carries the active treatment load at home, reinforcing what your in-office visits target from a different angle.`,
    moisturizer: `${name} keeps the barrier comfortable so stronger actives and procedures can work without unnecessary irritation.`,
    spf: `${name} is the guardrail for your full plan — it protects tone, texture, and treatment results from daily UV damage.`,
    mask: `${name} adds a periodic boost that complements facials and daily products between visits.`,
    scrub: `${name} supports texture refinement at home so professional exfoliation and renewal treatments stack more effectively.`,
    eye: `${name} addresses the eye area specifically, where skin is thinner and often needs its own targeted support.`,
    acne: `${name} keeps breakout control moving at home so office treatments are not doing all the work alone.`,
    brightening: `${name} handles at-home pigment control while procedures and SPF protect and refine from other angles.`,
    renewal: `${name} brings renewal into the daily routine, complementing peels, microneedling, and brightening steps.`,
    post_procedure: `${name} helps you recover smoothly after treatments and extends the visible benefits of in-office work.`,
    regimen: `${name} organizes your home care into one coordinated system aligned with your provider's recommendations.`,
    body: `${name} extends your facial plan to body areas where tone and texture also matter.`,
    hair: `${name} supports scalp and hair health alongside facial acne and congestion goals.`,
    fragrance: `${name} is an optional finishing layer — it does not change skin results but completes the experience your provider curated.`,
    prescription: `${name} adds prescription-strength fading support when over-the-counter brightening alone is not enough.`,
    wound_care: `${name} supports calm, clean healing around treatments so recovery stays comfortable and predictable.`,
    oil: `${name} adds nourishing renewal and glow without disrupting acne- or pigment-focused steps.`,
  };
  return map[role];
}

function buildSkincareNarrative(
  name: string,
  description: string,
): GravitasAestheticNarrative {
  const role = inferSkincareRole(name, description);
  return {
    reason: skincareReason(name, role),
    how: ensureSentence(description),
    expect: skincareExpect(name, role),
    fit: skincareFit(name, role),
  };
}

type ServiceNarrativeSeed = {
  names: string[];
  reason: string;
  how: string;
  expect: string;
  fit: string;
};

/** In-office services, IV offerings, and procedures — sourced from Gravitas 2026 services sheet. */
const GRAVITAS_SERVICE_NARRATIVE_SEEDS: ServiceNarrativeSeed[] = [
  {
    names: ["Glow N Go Facial"],
    reason:
      "Glow N Go Facial was recommended for a brighter, refreshed complexion with deep cleansing and relaxation built in.",
    how: "This 45-minute facial includes deep cleansing, epidermal sweep to remove dead skin and peach fuzz, facial massage, brow grooming, and a biocellulose mask for an instant glow.",
    expect:
      "You should leave with cleaner, smoother, more luminous skin and a polished brow shape. It is a great maintenance or pre-event refresh with no significant downtime.",
    fit: "Glow N Go Facial keeps skin bright and event-ready between deeper treatments in your plan.",
  },
  {
    names: ["Skin Vitalizing Experience"],
    reason:
      "Skin Vitalizing Experience was included to brighten, hydrate, and restore a youthful glow with a fully customized facial approach.",
    how: "This highly customized facial brightens and hydrates while therapeutic paraffin for hands and feet adds full-body relaxation during your visit.",
    expect:
      "Skin should feel deeply hydrated and instantly more radiant. The paraffin add-on leaves hands and feet exceptionally soft and relaxed.",
    fit: "This experience supports hydration and glow goals while giving your broader plan a restorative in-office boost.",
  },
  {
    names: ["Ultimate Skin Veneer Experience"],
    reason:
      "Ultimate Skin Veneer Experience was recommended to detoxify, tighten, and brighten skin while supporting collagen and pore refinement.",
    how: "The treatment removes impurities for instant hydration, tightening, and brightening while stimulating collagen, minimizing pores, and softening fine lines.",
    expect:
      "Skin should feel remarkably soft with a smoother, more youthful appearance after one visit. Results build best with the schedule your provider sets.",
    fit: "This is a high-impact facial that reinforces texture, firmness, and radiance goals across your full plan.",
  },
  {
    names: ["Ultimate Oxylux Experience"],
    reason:
      "Ultimate Oxylux Experience was added for advanced oxygenation, deep serum infusion, and comprehensive facial renewal.",
    how: "OxyGeneo exfoliates and oxygenates the skin, ultrasound helps nutrients absorb deeply, and Neo-Massage calms redness — plus epidermal sweep, extractions, LED therapy, and paraffin for hands and feet.",
    expect:
      "Expect deeply cleansed, oxygenated, and nourished skin with reduced puffiness and a refined glow. Extractions and LED support clarity without extended downtime.",
    fit: "Oxylux is the comprehensive facial chapter — it amplifies renewal, clarity, and recovery alongside your home routine.",
  },
  {
    names: ["Acne Treatment w/ Blue LED Light Therapy"],
    reason:
      "Acne Treatment w/ Blue LED Light Therapy was recommended to clear congestion, calm breakouts, and target acne-causing bacteria.",
    how: "Gentle dead-skin removal, thorough extractions, brow grooming, and an anti-acne mask are paired with high-frequency and blue LED therapy to reduce bacteria and inflammation.",
    expect:
      "Pores should feel clearer and skin calmer after the visit. Blue light and extractions work together for a healthier-looking complexion over a short series when needed.",
    fit: "This treatment handles in-office acne control while your home products maintain clarity between visits.",
  },
  {
    names: ["Rejuvenating Pumpkin Spice Experience w/ Head Massage"],
    reason:
      "Rejuvenating Pumpkin Spice Experience was included for seasonal renewal, deep relaxation, and stressed-skin recovery.",
    how: "A luxurious pumpkin spice facial is paired with a soothing medicated head massage designed to melt away tension while renewing the skin.",
    expect:
      "You should feel refreshed and revitalized with softer, brighter skin and noticeably reduced tension from the head massage.",
    fit: "This experience adds restorative renewal and stress relief — a supportive complement to active treatments in your plan.",
  },
  {
    names: ["Radiance Infusion Express"],
    reason:
      "Radiance Infusion Express was recommended for a fast, occasion-ready glow with deep cleanse and hydration.",
    how: "This advanced 3-in-1 express facial cleanses, exfoliates, oxygenates, and intensely hydrates in one session — removing dull buildup while infusing revitalizing nutrients.",
    expect:
      "Skin should look immediately brighter and more hydrated — ideal before a special event or when you need a quick glow refresh.",
    fit: "Radiance Infusion Express is your express glow step, keeping skin camera-ready between deeper plan chapters.",
  },
  {
    names: ["Radiant Neck Reset"],
    reason:
      "Radiant Neck Reset was added to renew the neck area where sun damage and aging often show first.",
    how: "The 3-in-1 neck treatment combines gentle exfoliation, natural oxygenation, and infusion of revitalizing nutrients for deep cleanse, exfoliation, and hydration.",
    expect:
      "The neck should feel smoother, more hydrated, and visibly refreshed. This is a focused reset for an area that daily skincare often misses.",
    fit: "Radiant Neck Reset extends your facial plan to the neck so results look cohesive from face to décolletage.",
  },
  {
    names: ["Underarm Glow Revival"],
    reason:
      "Underarm Glow Revival was recommended to lighten discoloration and improve uneven texture in the underarm area.",
    how: "The 3-in-1 underarm treatment exfoliates, oxygenates, and infuses revitalizing nutrients to address darkening and \"chicken skin\" texture.",
    expect:
      "Underarm skin should look brighter and feel smoother over a short series. Your provider will guide how many sessions fit your goals.",
    fit: "This targeted chapter addresses body-area tone and texture goals that complement your facial skincare plan.",
  },
  {
    names: ["LED Light Therapy"],
    reason:
      "LED Light Therapy was added to support collagen, clarity, or calming recovery depending on the wavelength your provider selected.",
    how: "Clinically proven LED wavelengths rejuvenate skin at a cellular level — red light stimulates collagen and firmness; blue light targets acne bacteria and inflammation.",
    expect:
      "This is a relaxing, non-invasive add-on with no downtime. Skin often looks calmer and more radiant after a series of sessions.",
    fit: "LED therapy reinforces renewal, acne control, or post-treatment recovery elsewhere in your plan.",
  },
  {
    names: ["Epidermal Sweep"],
    reason:
      "Epidermal Sweep was included to remove dead-skin buildup so products penetrate better and skin looks brighter.",
    how: "This safe exfoliation treatment removes dead skin and peach fuzz, promoting deeper product penetration and a smoother, brighter surface.",
    expect:
      "Skin should feel immediately smoother and look more luminous. It pairs especially well with masks, serums, and deeper facials.",
    fit: "Epidermal Sweep primes skin so every other step in your plan — home and in-office — performs more effectively.",
  },
  {
    names: ["Medical Extractions"],
    reason:
      "Medical Extractions were recommended to safely clear blackheads, whiteheads, and congested pores.",
    how: "A professional deep-cleansing procedure removes comedones and impacted pores with proper technique to minimize trauma while improving clarity.",
    expect:
      "Pores should feel clearer immediately. Some temporary redness is normal; skin typically looks smoother and healthier as it settles.",
    fit: "Extractions keep congestion under control so acne treatments and home products work on a cleaner foundation.",
  },
  {
    names: ["Paraffin Treatment (Hands and Feet)"],
    reason:
      "Paraffin Treatment was added for deep hydration and relaxation of tired hands and feet.",
    how: "Therapeutic warm paraffin seals in moisture, soothes discomfort, improves circulation, and leaves hands and feet exceptionally soft.",
    expect:
      "Hands and feet should feel deeply nourished, smooth, and relaxed after the treatment — ideal for dryness, roughness, or fatigue.",
    fit: "Paraffin adds restorative body comfort that pairs well with hydrating facials and wellness-focused visits.",
  },
  {
    names: ["Eyebrow Grooming"],
    reason:
      "Eyebrow Grooming was included to polish your brow shape and frame your features.",
    how: "Expert shaping and grooming defines brows for a cleaner, more polished look that complements your facial treatments.",
    expect:
      "Brows should look neatly shaped and balanced. This is a finishing detail that makes your overall result feel more put-together.",
    fit: "Brow grooming refines the framing of your face alongside skin treatments and optional permanent makeup.",
  },
  {
    names: ["Red Light Therapy"],
    reason:
      "Red Light Therapy was recommended for collagen support, inflammation relief, and whole-body wellness benefits.",
    how: "Therapeutic red and near-infrared light supports cellular repair, circulation, collagen production, and may assist lymphatic drainage and recovery — with no downtime.",
    expect:
      "Sessions are gentle and relaxing. Skin tone and firmness often improve over time, and many clients notice better recovery and overall wellness.",
    fit: "Red Light Therapy supports anti-aging skin goals and recovery alongside facials, microneedling, and IV wellness in your plan.",
  },
  {
    names: ["FREE Birthday Skin Cleanse"],
    reason:
      "Your complimentary Birthday Skin Cleanse celebrates your special month with a personalized refresh.",
    how: "A professional skin assessment leads into double cleanse, dead-skin removal, and a customized mask selected for your skin type to nourish, hydrate, calm, or purify.",
    expect:
      "Skin should feel clean, balanced, and refreshed — a relaxing self-care experience with a healthy glow for your birthday month.",
    fit: "This complimentary cleanse maintains skin health between the active chapters of your broader plan.",
  },
  {
    names: ["Skin Peeling"],
    reason:
      "Skin Peeling was recommended to visibly reduce hyperpigmentation, melasma, and uneven tone.",
    how: "This advanced depigmentation treatment inhibits tyrosinase to reduce excess melanin while supporting natural skin renewal for a brighter, more even complexion.",
    expect:
      "Pigment fades gradually over a series with proper home care and sun protection. Your provider will guide peeling depth and recovery timing.",
    fit: "Skin Peeling is the in-office pigment chapter — it works hand-in-hand with brightening home products and daily SPF.",
  },
  {
    names: ["Skinpen Microneedling"],
    reason:
      "Skinpen Microneedling was included to stimulate collagen and improve scars, lines, and uneven texture.",
    how: "Controlled microchannels trigger natural collagen and elastin production, improving acne scars, fine lines, wrinkles, and overall skin texture and radiance.",
    expect:
      "Expect mild redness and a sandpaper feel for a few days. Smoother, firmer skin builds over a series — typically several weeks between sessions.",
    fit: "Microneedling is the collagen-building chapter that renews texture while home recovery products support healing.",
  },
  {
    names: ["PRP Microneedling"],
    reason:
      "PRP Microneedling was recommended for enhanced regeneration using your own growth factors.",
    how: "Microneedling creates microchannels while platelet-rich plasma from your blood penetrates deeply — accelerating healing, boosting collagen, and improving scars, pores, and elasticity.",
    expect:
      "Recovery is similar to standard microneedling with potentially faster healing. Results build over a series for smoother, firmer, brighter skin.",
    fit: "PRP Microneedling is the advanced renewal chapter — it amplifies collagen goals alongside brightening and home recovery support.",
  },
  {
    names: ["Botox", "Botox 1-Unit"],
    reason:
      "Botox was recommended to soften dynamic wrinkles and expression lines while keeping results natural.",
    how: "Botox temporarily relaxes targeted facial muscles that cause forehead lines, frown lines, and crow's feet — smoothing expression wrinkles without a frozen look.",
    expect:
      "Results typically appear within a few days and last several months. Little to no downtime; you can return to normal activity right away.",
    fit: "Botox addresses movement-related lines while skincare and energy treatments support surface quality and collagen.",
  },
  {
    names: ["Ombre' Eyebrows Powdershading"],
    reason:
      "Ombre' Eyebrows Powdershading was recommended for beautifully shaped, low-maintenance brows that match your visit goals.",
    how: "This microshading technique creates a soft ombré effect — lighter at the front of the brows and gradually more defined toward the tail for a naturally polished look.",
    expect:
      "You'll wake up with shaped brows and skip the daily drawing routine. Your provider will walk you through healing, color settling, and any touch-up timing.",
    fit: "Permanent brow shading frames your face and simplifies daily routine — complementing skin treatments and facial balancing in your plan.",
  },
  {
    names: ["Natural Warts Removal"],
    reason:
      "Natural Warts Removal was included as a gentle, natural approach to reducing unwanted warts.",
    how: "Specialized natural extracts are carefully applied to help dry and break down warts over time with minimal discomfort and reduced scarring risk.",
    expect:
      "Warts gradually diminish over one or more sessions depending on size and location. Your provider will set expectations for your specific case.",
    fit: "This targeted procedure clears a specific concern so the rest of your aesthetic plan can stay focused on skin quality and renewal.",
  },
];

function buildIvNarrative(
  name: string,
  description: string,
): GravitasAestheticNarrative {
  const isPackage = /package|8-week|8 week/i.test(name);
  const isShot = /shot|lipomelt|mic b12/i.test(name);
  if (isShot) {
    return {
      reason: `${name} was recommended to support metabolism, energy, and wellness goals discussed during your visit.`,
      how: ensureSentence(
        description ||
          "This wellness injection combines lipotropic nutrients and B12 to support fat metabolism, energy, and metabolic health.",
      ),
      expect:
        "Many clients notice improved energy and mental clarity within days. Your provider will guide frequency based on your goals and candidacy.",
      fit: `${name} adds an inside-out wellness boost that complements skin-focused IV drips and your broader aesthetic plan.`,
    };
  }
  return {
    reason: `${name} was included to support wellness, hydration, and skin radiance from within.`,
    how: ensureSentence(description),
    expect: isPackage
      ? "Weekly sessions over the package duration deliver the most consistent brightening, energy, and immune support. Your provider will schedule each infusion."
      : "You may notice improved hydration, energy, and skin glow within hours to days. A series often delivers the most lasting wellness and complexion benefits.",
    fit: `${name} nourishes your plan from the inside out — supporting collagen, detox, and vitality alongside topical and in-office skin treatments.`,
  };
}

function buildNarrativeIndex(): Map<string, GravitasAestheticNarrative> {
  const index = new Map<string, GravitasAestheticNarrative>();

  const add = (names: string[], narrative: GravitasAestheticNarrative) => {
    for (const name of names) {
      const key = normalizeGravitasAiKey(name);
      if (key) index.set(key, narrative);
    }
  };

  for (const product of GRAVITAS_SKINCARE_PRODUCTS) {
    add(
      [product.name],
      buildSkincareNarrative(product.name, product.description ?? product.name),
    );
  }

  add(
    ["Silky Smooth Plus", "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)"],
    buildSkincareNarrative(
      "Silky Smooth Plus (Retinol w/ Bakuchiol Serum)",
      GRAVITAS_SKINCARE_PRODUCTS.find((p) => p.name.includes("Silky Smooth"))?.description ??
        "Triple-action retinoid and bakuchiol serum for renewal.",
    ),
  );

  for (const seed of GRAVITAS_SERVICE_NARRATIVE_SEEDS) {
    const narrative: GravitasAestheticNarrative = {
      reason: seed.reason,
      how: ensureSentence(seed.how),
      expect: seed.expect,
      fit: seed.fit,
    };
    add(seed.names, narrative);
  }

  for (const offering of GRAVITAS_IV_WELLNESS_OFFERINGS) {
    add([offering.name], buildIvNarrative(offering.name, offering.description));
  }

  add(
    [
      "Fountain of Youth 5K (Vit C 5000mg) or 15K (Vit C 15,000mg)",
      "Fountain of Youth 5K (Vit C 5000mg)",
      "Fountain of Youth 15K (Vit C 15,000mg)",
    ],
    buildIvNarrative(
      "Fountain of Youth 5K (Vit C 5000mg)",
      GRAVITAS_IV_WELLNESS_OFFERINGS.find((o) => o.name.includes("Fountain of Youth 5K"))
        ?.description ?? "",
    ),
  );

  return index;
}

const GRAVITAS_NARRATIVE_INDEX = buildNarrativeIndex();

export function lookupGravitasAestheticNarrative(
  rawName: string | null | undefined,
): GravitasAestheticNarrative | null {
  const name = (rawName ?? "").trim();
  if (!name) return null;

  const key = normalizeGravitasAiKey(name);
  const direct = GRAVITAS_NARRATIVE_INDEX.get(key);
  if (direct) return direct;

  for (const [entryKey, narrative] of GRAVITAS_NARRATIVE_INDEX) {
    if (key.includes(entryKey) || entryKey.includes(key)) return narrative;
  }

  return null;
}

export function getGravitasChapterNarrative(
  chapter: TreatmentChapter,
): GravitasAestheticNarrative | null {
  const candidates = [
    chapter.displayName,
    ...chapter.planItems.map((item) => item.product ?? ""),
  ]
    .map((value) => value.trim())
    .filter(Boolean);

  const seen = new Set<string>();
  for (const candidate of candidates) {
    const norm = normalizeGravitasAiKey(candidate);
    if (seen.has(norm)) continue;
    seen.add(norm);
    const hit = lookupGravitasAestheticNarrative(candidate);
    if (hit) return hit;
  }

  return null;
}
